import React, { useEffect, useState } from 'react';
import { useTranslation } from '../../i18n';
import { useTimers } from '../../contexts/TimerContext';
import { elapsedSecondsSince, formatClock, isLongRunning } from '../../utils/timer';
import Icon from '../Icon';
import './RunningTimerBar.css';

interface RunningTimerBarProps {
  /** Timely page shows every timer inline, so the bar would only duplicate it. */
  hidden?: boolean;
}

const RunningTimerBar: React.FC<RunningTimerBarProps> = ({ hidden = false }) => {
  const { t } = useTranslation();
  const { running, stopTimer } = useTimers();
  const [now, setNow] = useState(() => Date.now());
  const [isStopping, setIsStopping] = useState(false);

  useEffect(() => {
    if (running.length === 0 || hidden) return;
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [running.length, hidden]);

  if (hidden || running.length === 0) return null;

  const [primary] = running;
  const elapsed = elapsedSecondsSince(primary.startedAt, now);
  const longRunning = isLongRunning(elapsed);
  const others = running.length - 1;

  const handleStop = async () => {
    setIsStopping(true);
    await stopTimer(primary.todoId);
    setIsStopping(false);
  };

  return (
    <div className={`running-timer-bar${longRunning ? ' is-long' : ''}`} role="status" aria-live="off">
      <a className="running-timer-bar__link" href="#/timely" aria-label={t('timer.openTimely')}>
        <span className="running-timer-bar__dot" aria-hidden="true" />
        <span className="running-timer-bar__clock">{formatClock(elapsed)}</span>
        <span className="running-timer-bar__title">{primary.title}</span>
        {others > 0 && <span className="running-timer-bar__more">{t('timer.moreRunning', { count: others })}</span>}
      </a>
      {longRunning && (
        <span className="running-timer-bar__warning">
          <Icon name="warning" />
          <span>{t('timer.longRunningShort')}</span>
        </span>
      )}
      <button
        type="button"
        className="running-timer-bar__stop"
        onClick={handleStop}
        disabled={isStopping}
        aria-label={`${t('timer.stop')}: ${primary.title}`}
      >
        <span className="running-timer-bar__stop-icon" aria-hidden="true" />
        <span className="running-timer-bar__stop-label">{t('timer.stop')}</span>
      </button>
    </div>
  );
};

export default RunningTimerBar;
